import type { TFunction } from 'i18next';
import type { Query, QueryClient, InfiniteData } from '@tanstack/react-query';

import { useRef, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';

import { toast } from '@nexus/ari-lib/components/snackbar';

import { toFolderEntry, toDocumentItem } from './documents-data';
import { isPending, previewSource, PENDING_REFETCH_MS } from './documents-processing';
import {
  emptyBin,
  retryFile,
  updateFile,
  deleteItems,
  fetchUsage,
  createFolder,
  fetchFolders,
  fetchLibrary,
  restoreItems,
  updateFolder,
  fetchFileLink,
  fetchTableLink,
  fetchPreviewLink,
  fetchDocumentsByIds,
} from './documents-api';

import type { ItemIds, LibraryQuery } from './documents-api';
import type { PreviewSource } from './documents-processing';
import type { DocumentItem, FolderEntry } from './documents-data';
import type { ItemResult, ItemOutcome, LibraryEntry, LibraryResponse, OutcomesResponse } from './documents-api-types';

export const DOCUMENTS_KEY = ['documents'] as const;
export const USAGE_KEY = [...DOCUMENTS_KEY, 'usage'] as const;
export const UNFINISHED_KEY = [...DOCUMENTS_KEY, 'unfinished'] as const;
export const FOLDERS_KEY = [...DOCUMENTS_KEY, 'folders'] as const;
export const LIBRARY_KEY = [...DOCUMENTS_KEY, 'library'] as const;

const RESIGN_EARLY_MS = 60 * 1000;
/** The most ids one library request takes. */
const MAX_POLLED_IDS = 50;
const PARALLEL_MOVES = 6;
const MAX_NAMED = 3;

// Refetching these signs a new link or reads a sheet again. CLOUD-1159.
const OPEN_PREVIEW_KEYS = new Set(['link', 'sheets']);

/** Everything but the open preview and source. A new link starts a video or a PDF again from the top. */
export function refreshListing(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    queryKey: DOCUMENTS_KEY,
    predicate: (q) => !OPEN_PREVIEW_KEYS.has(String(q.queryKey[1])) && q.queryKey[2] !== 'source',
  });
}

function linkLifetimeMs(seconds: number | undefined): number {
  return Math.max((seconds ?? 3600) * 1000 - RESIGN_EARLY_MS, 30 * 1000);
}

function splitEntries(pages: LibraryResponse[] | undefined) {
  const seen = new Set<string>();
  const folders: FolderEntry[] = [];
  const documents: DocumentItem[] = [];

  for (const entry of pages?.flatMap((page) => page.items) ?? []) {
    if (seen.has(entry.id)) continue;
    seen.add(entry.id);
    if (entry.type === 'folder') folders.push(toFolderEntry(entry));
    else documents.push(toDocumentItem(entry));
  }

  return { folders, documents };
}

export function pendingIds(pages: LibraryResponse[] | undefined): string[] {
  const ids = pages?.flatMap((page) =>
    page.items.filter((entry) => entry.type === 'document' && isPending(entry)).map((entry) => entry.id)
  );
  return [...new Set(ids)].slice(0, MAX_POLLED_IDS);
}

/** A file still being read keeps its card picture, so the card does not load it again every poll. */
export function mergePolled(pages: LibraryResponse[], polled: LibraryEntry[]): LibraryResponse[] {
  const byId = new Map(polled.map((entry) => [entry.id, entry]));
  return pages.map((page) => ({
    ...page,
    items: page.items.map((entry) => {
      const fresh = byId.get(entry.id);
      if (!fresh) return entry;
      // The poll asks by id, which does not say when the file was last used.
      const kept = { ...fresh, usedAt: entry.usedAt };
      return isPending(fresh) ? { ...kept, previewUrl: entry.previewUrl, pageAspect: entry.pageAspect } : kept;
    }),
  }));
}

export function useDocumentsLibrary(query: LibraryQuery) {
  const result = useInfiniteQuery<
    LibraryResponse,
    Error,
    InfiniteData<LibraryResponse, number>,
    readonly unknown[],
    number
  >({
    queryKey: [...LIBRARY_KEY, query],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => fetchLibrary(query, pageParam),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.items.length, 0);
      if (lastPage.items.length === 0 || loaded >= lastPage.total) return undefined;
      return loaded;
    },
    // Recents changes with every open, and an open does not touch this cache.
    staleTime: (q) => (query.view === 'used' ? 0 : linkLifetimeMs(q.state.data?.pages[0]?.linksExpireInSeconds)),
    refetchOnWindowFocus: true,
  });

  const pages = result.data?.pages;
  const { folders, documents } = useMemo(() => splitEntries(pages), [pages]);

  // Only the files still being read are asked for again. The whole listing signs every card's picture anew.
  const queryClient = useQueryClient();
  const polledIds = useMemo(() => pendingIds(pages), [pages]);
  const listingKey = useMemo(() => [...LIBRARY_KEY, query], [query]);
  useQuery({
    queryKey: [...LIBRARY_KEY, 'pending', polledIds],
    enabled: polledIds.length > 0,
    refetchInterval: PENDING_REFETCH_MS,
    queryFn: async () => {
      const { items } = await fetchDocumentsByIds(polledIds);
      // A file deleted in another tab is gone from the answer, so the listing is read again.
      if (items.length < polledIds.length) void queryClient.invalidateQueries({ queryKey: listingKey, exact: true });
      queryClient.setQueryData<InfiniteData<LibraryResponse, number>>(listingKey, (data) =>
        data ? { ...data, pages: mergePolled(data.pages, items) } : data
      );
      return items.length;
    },
  });

  // Reading a file counts against the month, so usage changes when processing ends.
  const pending = polledIds.length > 0;
  const wasPending = useRef(pending);
  useEffect(() => {
    if (wasPending.current && !pending) queryClient.invalidateQueries({ queryKey: USAGE_KEY });
    wasPending.current = pending;
  }, [pending, queryClient]);

  return {
    folders,
    documents,
    isLoading: result.isLoading,
    error: pages ? null : result.error,
    fetchNextPage: result.fetchNextPage,
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
  };
}

export function useDocumentFolders() {
  return useQuery({ queryKey: FOLDERS_KEY, queryFn: fetchFolders });
}

export function useDocumentsUsage() {
  return useQuery({ queryKey: USAGE_KEY, queryFn: fetchUsage });
}

function fetchLink(id: string, source: PreviewSource) {
  if (source === 'pdf') return fetchPreviewLink(id);
  return source === 'table' ? fetchTableLink(id) : fetchFileLink(id, 'inline');
}

/** `override` asks for the PDF of a sheet whose table cannot be shown. */
export function useDocumentLink(item: DocumentItem | null, override?: PreviewSource) {
  const source = override ?? (item ? previewSource(item) : 'none');
  return useQuery({
    // A replacement that becomes the shown version needs a link to the new file.
    queryKey: [...DOCUMENTS_KEY, 'link', item?.id, source, item?.replacing, item?.modifiedAt],
    queryFn: () => fetchLink(item?.id ?? '', source),
    enabled: source === 'original' || source === 'table' || source === 'pdf',
    staleTime: (q) => linkLifetimeMs(q.state.data?.expiresInSeconds),
  });
}

function isNameTaken(error: unknown): boolean {
  return (error as { code?: string } | null)?.code === 'name_taken';
}

function failureMessage(error: unknown, fallback: string): string {
  return isNameTaken(error) ? 'That name is already used here.' : fallback;
}

function quotedList(names: string[]): string {
  const quoted = names.map((name) => `“${name}”`);
  return quoted.length < 2 ? quoted.join('') : `${quoted.slice(0, -1).join(', ')} and ${quoted.at(-1)}`;
}

function refusedMoveMessage(names: Array<string | undefined>): string {
  const known = names.filter((name): name is string => name !== undefined);
  const which =
    known.length < names.length || names.length > MAX_NAMED ? plural(names.length, 'file') : quotedList(known);
  return names.length === 1
    ? `Could not move ${which}. A file with this name already exists in the destination folder.`
    : `Could not move ${which}. Files with these names already exist in the destination folder.`;
}

function countOutcomes(response: OutcomesResponse, outcomes: ItemOutcome[]): number {
  return response.results.filter((result) => outcomes.includes(result.outcome)).length;
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

/** One item says its new name and where. Several give a count. CLOUD-1318. */
export function renamedOnRestoreMessage(results: ItemResult[], t: TFunction): string | null {
  const renamed = results.filter((result) => result.name && result.previousName && result.folderName !== undefined);
  if (renamed.length > 1) return t('documents.restore.renamedMany', { count: renamed.length });
  const [item] = renamed;
  if (!item) return null;
  const key = item.type === 'folder' ? 'documents.restore.renamedFolder' : 'documents.restore.renamedFile';
  return t(key, {
    name: item.previousName,
    newName: item.name,
    folder: item.folderName ?? t('documents.bin.topLevel'),
  });
}

type LibraryPages = InfiniteData<LibraryResponse, number>;

function isBinListing(query: Query): boolean {
  const listing = query.queryKey[2];
  return typeof listing === 'object' && listing !== null && (listing as LibraryQuery).view === 'bin';
}

const BIN_LISTINGS = { queryKey: LIBRARY_KEY, predicate: isBinListing };

export function useDocumentsActions() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const refresh = () => refreshListing(queryClient);
  // A deleted or restored file changes what an open source can show.
  const refreshAll = () => queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEY });

  const createFolderMutation = useMutation({
    mutationFn: ({ name, parentId }: { name: string; parentId: string | null }) => createFolder(name, parentId),
    onSuccess: ({ folder, renamed }) => {
      if (renamed) toast.info(`Created as “${folder.name}”.`);
      return refresh();
    },
    onError: (error) => toast.error(failureMessage(error, 'Could not create the folder.')),
  });

  const renameFolder = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateFolder(id, { name }),
    onSuccess: refresh,
    onError: (error) => toast.error(failureMessage(error, 'Could not rename the folder.')),
  });

  const moveFolder = useMutation({
    mutationFn: ({ id, parentId }: { id: string; parentId: string | null }) => updateFolder(id, { parentId }),
    onSuccess: ({ folder, renamed }) => {
      if (renamed) toast.info(`Moved as “${folder.name}”.`);
      return refresh();
    },
    onError: () => toast.error('Could not move the folder.'),
  });

  const renameFile = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateFile(id, { name }),
    onSuccess: refresh,
    onError: (error) => toast.error(failureMessage(error, 'Could not rename the file.')),
  });

  const moveFiles = useMutation({
    mutationFn: async ({
      files,
      folderId,
    }: {
      files: Array<{ id: string; name?: string }>;
      folderId: string | null;
    }) => {
      const rejected: Array<{ name?: string; reason: unknown }> = [];
      for (let i = 0; i < files.length; i += PARALLEL_MOVES) {
        const batch = files.slice(i, i + PARALLEL_MOVES);
        const results = await Promise.allSettled(batch.map((file) => updateFile(file.id, { folderId })));
        results.forEach((result, j) => {
          if (result.status === 'rejected') rejected.push({ name: batch[j].name, reason: result.reason });
        });
      }
      const refused = rejected.filter((r) => isNameTaken(r.reason)).map((r) => r.name);
      return { failed: rejected.length - refused.length, refused };
    },
    onSuccess: ({ failed, refused }, { files }) => {
      if (refused.length) toast.error(refusedMoveMessage(refused));
      if (failed) toast.error(`Could not move ${failed} of ${plural(files.length, 'item')}.`);
      return refresh();
    },
    onError: () => toast.error('Could not move those items.'),
  });

  const softDelete = useMutation({
    mutationFn: (ids: ItemIds) => deleteItems(ids),
    onSuccess: (response) => {
      const failed = response.results.length - countOutcomes(response, ['deleted', 'already_in_bin']);
      if (failed) toast.error(`Could not delete ${plural(failed, 'item')}.`);
      return refreshAll();
    },
    onError: () => toast.error('Could not delete those items.'),
  });

  const purge = useMutation({
    mutationFn: (ids: ItemIds) => deleteItems(ids, true),
    onSuccess: (response) => {
      const failed = response.results.length - countOutcomes(response, ['purging', 'deleted']);
      if (failed) toast.error(`Could not delete ${plural(failed, 'item')}.`);
      return refreshAll();
    },
    onError: () => toast.error('Could not delete those items.'),
  });

  const restore = useMutation({
    mutationFn: (ids: ItemIds) => restoreItems(ids),
    onSuccess: (response) => {
      const toTop = countOutcomes(response, ['restored_to_top']);
      const failed = response.results.length - toTop - countOutcomes(response, ['restored']);
      const renamed = renamedOnRestoreMessage(response.results, t);
      if (toTop) toast.info(`Restored ${plural(toTop, 'item')} to the top level. The folder is gone.`);
      if (renamed) toast.info(renamed);
      if (failed) toast.error(`Could not restore ${plural(failed, 'item')}.`);
      return refreshAll();
    },
    onError: () => toast.error('Could not restore those items.'),
  });

  // The bin shows empty at once. It comes back when the request fails. CLOUD-1312.
  const empty = useMutation({
    mutationFn: emptyBin,
    onMutate: async () => {
      await queryClient.cancelQueries(BIN_LISTINGS);
      const before = queryClient.getQueriesData<LibraryPages>(BIN_LISTINGS);
      queryClient.setQueriesData<LibraryPages>(BIN_LISTINGS, (data) =>
        data ? { ...data, pages: data.pages.map((page) => ({ ...page, items: [], total: 0 })) } : data
      );
      return { before };
    },
    onError: (_error, _variables, context) => {
      context?.before.forEach(([key, data]) => queryClient.setQueryData(key, data));
      toast.error('Could not empty the bin.');
    },
    onSettled: refreshAll,
  });

  const retry = useMutation({
    mutationFn: (id: string) => retryFile(id),
    onSuccess: refresh,
    onError: (error) => {
      const notRetryable = (error as { code?: string } | null)?.code === 'not_retryable';
      toast.error(notRetryable ? 'Ari cannot read this file again now.' : 'Could not retry the file.');
      return refresh();
    },
  });

  return {
    createFolder: createFolderMutation,
    renameFolder,
    moveFolder,
    renameFile,
    moveFiles,
    softDelete,
    purge,
    restore,
    empty,
    retry,
  };
}
